import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material'
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react'
import colors from '../../constants/colors'
import CardTAG from '../CardTAG'
import TextEditor from '../TextEditor'
import UploadDropzone from '../UploadDropzone'
import uploadConstants from '../UploadDropzone/constants'
import { uploadInputZones } from '../UploadDropzone/types'
import { ReactComponent as ArrowIcon } from '../../assets/images/arrowIcon.svg'
import FormLayout from '../forms/FormLayout'
import DividerTAG from '../DividerTAG'
import utils from '../CaseInvestigation/utils'
import TableTAG from '../TableTAG'
import { ReactComponent as DocumentIcon } from '../../assets/images/DocumentIcon.svg'

import TextFieldsIcon from '@mui/icons-material/TextFields'
import { useSelector } from 'react-redux'
import authSelectors from '../../store/auth/authSelectors'
const selectStyle = {
  '&.MuiSelect-outlined , .MuiOutlinedInput-input': {
    p: '.8rem',
    paddingLeft: '2rem',
    marginTop: 0,
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.WHITE,
  },
  '&.MuiSelect-root , .MuiSelect-icon': {
    top: 'auto !important',
    right: '1.2rem',
    fill: colors.TAG_LINES_GRAY,
  },
  minWidth: '24rem',
  width: '100%',
  height: '100%',
  borderRadius: 0,
  justifyContent: 'center',
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '.MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}
const boxStyle = { width: '2rem', height: '2.4rem' }
const iconStyle = { width: '100%', height: '100%', color: colors.TAG_BLUE }

const renderUploadedFiles = (files: File[], deleteFile: any) => {
  return (
    <TableTAG
      pagination={false}
      columns={[
        {
          id: 'preview',
          label: 'Preview',
          format: (_: any, row: any) => {
            return (
              <Box sx={[boxStyle]}>
                {row?.link ? <a href={row?.link} target="_blank" rel="noreferrer"><DocumentIcon width='100%' height='100%' fill={colors.TAG_BLUE} /></a> : <DocumentIcon width='100%' height='100%' fill={colors.TAG_BLUE} />}
                
              </Box>
            )
          },
        },
        {
          id: 'name',
          label: 'File Name',
          format: (_: any, row: any) => {
            return <Box sx={{ overflow: 'hidden', maxWidth: '20%' }}>{row?.name}</Box>
          },
        },
        // { id: 'type', label: 'File Type' },
        {
          id: 'editFile',
          label: 'Edit File',
          format: (_: any, row: any) => {
            return (
              <Box>
                <Button
                  onClick={e => {
                    e.preventDefault()
                    deleteFile(row?.id ? row?.id : row?.name)
                  }}
                  variant='outlined'
                >
                  <Typography variant='bodyRegular' color='inherit'>
                    Delete
                  </Typography>
                </Button>
              </Box>
            )
          },
        },
      ]}
      rows={files}
    />
  )
}

const renderSelectOptions = (options: { value: string; text: string }[]) => {
  return options.map((option, index) => {
    return (
      <MenuItem value={option.value} key={`${option.text}-${index}`}>
        <Typography variant='h4'>{option.text}</Typography>
      </MenuItem>
    )
  })
}

const renderCheckboxes = (
  options: { value: string; text: string; checked: boolean }[],
  outcomesChangeHandler:
    | ((event: ChangeEvent<HTMLInputElement>, checked: boolean) => void)
    | undefined,
  previewOnly = false,
) => {
  return options.map((option, index) => {
    return (
      <FormControlLabel
        control={
          <Checkbox
            value={option.value}
            checked={option.checked}
            onChange={outcomesChangeHandler}
            disabled={previewOnly}
          />
        }
        label={<Typography variant='boldTextMedium'>{option.text}</Typography>}
        key={`${option?.value}-${index}`}
      />
    )
  })
}

export interface ICaseResolveDataPart {
  dispositionData: { value: string; text: string }[]
  outcomesData: { value: string; text: string; checked: boolean }[]
  dispositionHandler: (e: SelectChangeEvent) => void
  selectedDisposition: string
  outcomesChangeHandler: (event: React.ChangeEvent<HTMLInputElement>) => void
  setUploadedFiles: Dispatch<SetStateAction<File[]>>
  uploadedFiles: File[]
  setDescription: any
  description: any
}

const CaseResolveDataPart: React.FC<ICaseResolveDataPart> = ({
  dispositionData,
  outcomesData,
  dispositionHandler,
  selectedDisposition,
  outcomesChangeHandler,
  uploadedFiles,
  setUploadedFiles,
  setDescription,
  description,
}) => {
  const userEditAccess = useSelector(authSelectors.userReportEditAccess)

  const handleUpload = (newFile: File[]) => {
    setUploadedFiles(prev => {
      if (prev?.length > 0) {
        return [...prev, ...newFile]
      }
      return newFile
    })
  }
  const removeSelectedFile = (fileName: string) => {
    setUploadedFiles(prev => prev.filter((file: File) => file?.name !== fileName))
  }

  return (
    <>
      <Stack sx={{ mt: { xs: 2, md: 0 }, display: 'flex', flexDirection: 'column', gap: '3.6rem' }}>
        
        <CardTAG title='Resolution Explanation'>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2.5rem', width: '100%' }}>
            {userEditAccess ? 
            <>
              {uploadedFiles.length == 0 && <UploadDropzone
                onChange={handleUpload}
                iconAndText={uploadConstants.getDropzoneIconsAndText({
                  type: uploadInputZones.DOCUMENT,
                  themeColor: colors.TAG_BLUE,
                })}
                acceptType={uploadConstants.dropzoneAcceptanceType[uploadInputZones.DOCUMENT]}
                mainColor={colors.TAG_BLUE}
              />}
              <TextEditor setText={setDescription as any} text={description} />
            </> : <>{description}</>}
            {uploadedFiles.length !== 0 && renderUploadedFiles(uploadedFiles, removeSelectedFile)}
          </Box>
        </CardTAG>
        
        {dispositionData && (
          <CardTAG
            title='What was the disposition of the case?'
            cardAction={
              <Select
                onChange={dispositionHandler}
                IconComponent={ArrowIcon}
                displayEmpty
                renderValue={
                  selectedDisposition !== ''
                    ? undefined
                    : () => (
                        <Typography variant='bodyRegular' color={colors.TAG_TEXT_GRAY}>
                          {'Answer Here'}
                        </Typography>
                      )
                }
                value={selectedDisposition}
                sx={selectStyle}
                disabled={!userEditAccess}
              >
                {renderSelectOptions(dispositionData)}
              </Select>
            }
          />
        )}

        {outcomesData && (
          <CardTAG title='Outcomes'>
            <FormLayout>
              <Typography variant='labelBold'>Select all that apply</Typography>
              <DividerTAG />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {renderCheckboxes(outcomesData, outcomesChangeHandler, !userEditAccess)}
              </Box>
            </FormLayout>
          </CardTAG>
        )}
      </Stack>
    </>
  )
}

export default CaseResolveDataPart
